<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="visible"
    width="900px"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    @close="handleClose"
    custom-class="contract-detail-dialog"
  >
    <!-- 合同基本信息 -->
    <el-card class="detail-section" shadow="never">
      <div slot="header" class="clearfix">
        <span class="section-title">合同基本信息</span>
      </div>
      
      <el-descriptions :column="2" border>
        <el-descriptions-item label="合同编号">
          <span class="detail-value">{{ contractData.contractNo }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="合同主体">
          <span class="detail-value">{{ contractData.contractSubject }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="签约开始日期">
          <span class="detail-value date-value">{{ contractData.signStartDate }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="签约结束日期">
          <span class="detail-value date-value">{{ contractData.signEndDate }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="合同期限">
          <span class="detail-value">{{ calculateContractPeriod }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 租赁信息 -->
    <el-card class="detail-section" shadow="never">
      <div slot="header" class="clearfix">
        <span class="section-title">租赁信息</span>
      </div>
      
      <el-descriptions :column="2" border>
        <el-descriptions-item label="租赁楼宇">
          <span class="detail-value">{{ contractData.leaseBuilding || '未填写' }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="租赁位置">
          <span class="detail-value">{{ contractData.leaseLocation || '未填写' }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="租赁面积">
          <span class="detail-value">{{ formatNumber(contractData.leaseArea) }} ㎡</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="租金单价">
          <span class="detail-value">{{ formatNumber(contractData.firstYearRentUnitPrice) }} 元/㎡/月</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="租金政策" :span="2">
          <span class="detail-value">{{ contractData.rentPolicy || '未填写' }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="支付方式">
          <el-tag 
            :type="getPaymentMethodTagType(contractData.rentPaymentMethod)"
            size="medium"
          >
            {{ contractData.rentPaymentMethod || '未设置' }}
          </el-tag>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 财务信息 -->
    <el-card class="detail-section" shadow="never">
      <div slot="header" class="clearfix">
        <span class="section-title">财务信息</span>
      </div>
      
      <el-descriptions :column="2" border>
        <el-descriptions-item label="首年租金">
          <span class="detail-value money-value">{{ formatCurrency(contractData.firstYearRent) }} 元</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="月租金">
          <span class="detail-value money-value">{{ formatCurrency(monthlyRent) }} 元</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="押金金额">
          <span class="detail-value money-value">{{ formatCurrency(contractData.depositAmount) }} 元</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="押金比例">
          <span class="detail-value">{{ depositRatio }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 其他信息 -->
    <el-card class="detail-section" shadow="never">
      <div slot="header" class="clearfix">
        <span class="section-title">其他信息</span>
      </div>
      
      <el-descriptions :column="1" border>
        <el-descriptions-item label="开票信息">
          <div class="multi-line-text">
            {{ contractData.invoiceInfo || '未填写' }}
          </div>
        </el-descriptions-item>
        
        <el-descriptions-item label="备注">
          <div class="multi-line-text">
            {{ contractData.remark || '无备注' }}
          </div>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 系统信息 -->
    <el-card class="detail-section" shadow="never">
      <div slot="header" class="clearfix">
        <span class="section-title">系统信息</span>
      </div>
      
      <el-descriptions :column="2" border>
        <el-descriptions-item label="创建时间">
          <span class="detail-value">{{ formatDateTime(contractData.createTime) }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="更新时间">
          <span class="detail-value">{{ formatDateTime(contractData.updateTime) }}</span>
        </el-descriptions-item>
        
        <el-descriptions-item label="合同ID">
          <span class="detail-value">{{ contractData.contractid }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 合同状态提示 -->
    <div class="status-info" v-if="contractStatus">
      <el-alert
        :title="contractStatus.title"
        :type="contractStatus.type"
        :description="contractStatus.description"
        show-icon
        :closable="false"
      />
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
      <el-button 
        type="primary" 
        v-if="hasPrintPermission"
        @click="handlePrint"
      >
        <i class="el-icon-printer"></i> 打印
      </el-button>
      <el-button 
        type="success" 
        @click="handleExportDetail"
      >
        <i class="el-icon-download"></i> 导出详情
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'ContractDetailDialog',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    contractData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // 权限控制（可根据实际需求调整）
      hasPrintPermission: true
    }
  },
  computed: {
    dialogTitle() {
      return `合同详情 - ${this.contractData.contractNo || ''}`
    },
    
    // 计算合同期限
    calculateContractPeriod() {
      if (!this.contractData.signStartDate || !this.contractData.signEndDate) {
        return '未设置'
      }
      
      try {
        const startDate = dayjs(this.contractData.signStartDate)
        const endDate = dayjs(this.contractData.signEndDate)
        const years = endDate.diff(startDate, 'year')
        const months = endDate.diff(startDate, 'month') % 12
        
        let period = ''
        if (years > 0) {
          period += `${years}年`
        }
        if (months > 0) {
          period += `${months}个月`
        }
        
        return period || '不足1个月'
      } catch (error) {
        console.error('计算合同期限错误:', error)
        return '计算错误'
      }
    },
    
    // 计算月租金
    monthlyRent() {
      if (this.contractData.firstYearRent) {
        return Number(this.contractData.firstYearRent) / 12
      }
      return 0
    },
    
    // 计算押金比例
    depositRatio() {
      if (this.contractData.firstYearRent && this.contractData.depositAmount) {
        const ratio = (Number(this.contractData.depositAmount) / Number(this.contractData.firstYearRent)) * 100
        return `${ratio.toFixed(1)}%`
      }
      return '未计算'
    },
    
    // 合同状态判断
    contractStatus() {
      if (!this.contractData.signStartDate || !this.contractData.signEndDate) {
        return null
      }
      
      const today = dayjs()
      const startDate = dayjs(this.contractData.signStartDate)
      const endDate = dayjs(this.contractData.signEndDate)
      
      if (today.isBefore(startDate)) {
        return {
          title: '合同未开始',
          type: 'info',
          description: `合同将于 ${startDate.format('YYYY-MM-DD')} 开始生效`
        }
      } else if (today.isAfter(endDate)) {
        return {
          title: '合同已过期',
          type: 'warning',
          description: `合同已于 ${endDate.format('YYYY-MM-DD')} 到期`
        }
      } else {
        const daysRemaining = endDate.diff(today, 'day')
        if (daysRemaining <= 30) {
          return {
            title: '合同即将到期',
            type: 'error',
            description: `合同将于 ${endDate.format('YYYY-MM-DD')} 到期，剩余 ${daysRemaining} 天`
          }
        } else if (daysRemaining <= 90) {
          return {
            title: '合同即将到期',
            type: 'warning',
            description: `合同将于 ${endDate.format('YYYY-MM-DD')} 到期，剩余 ${daysRemaining} 天`
          }
        } else {
          return {
            title: '合同有效期内',
            type: 'success',
            description: `合同有效期至 ${endDate.format('YYYY-MM-DD')}`
          }
        }
      }
    }
  },
  methods: {
    // 格式化数字
    formatNumber(value) {
      if (value == null || value === '') return '0.00'
      const num = Number(value)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    // 格式化货币
    formatCurrency(value) {
      if (value == null || value === '') return '0.00'
      const num = Number(value)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('zh-CN', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    // 格式化日期时间
    formatDateTime(value) {
      if (!value) return '未知'
      try {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
      } catch (error) {
        return value
      }
    },
    
    // 获取支付方式标签类型
    getPaymentMethodTagType(method) {
      const map = {
        '月付': 'primary',
        '季付': 'success',
        '半年付': 'warning',
        '年付': 'danger',
        '一次性支付': 'info'
      }
      return map[method] || 'info'
    },
    
    // 处理关闭
    handleClose() {
      this.$emit('close')
    },
    
    // 打印合同
    handlePrint() {
      this.$message.info('打印功能正在开发中...')
      // 实际开发中可以调用 window.print() 或专门的打印组件
    },
    
    // 导出合同详情
    handleExportDetail() {
      const content = this.generateExportContent()
      this.downloadFile(content, `合同详情_${this.contractData.contractNo}_${dayjs().format('YYYYMMDDHHmmss')}.txt`)
      this.$message.success('导出成功')
    },
    
    // 生成导出内容
    generateExportContent() {
      const lines = []
      lines.push('='.repeat(50))
      lines.push(`合同详情 - ${this.contractData.contractNo}`)
      lines.push('='.repeat(50))
      lines.push('')
      lines.push('【合同基本信息】')
      lines.push(`合同编号：${this.contractData.contractNo}`)
      lines.push(`合同主体：${this.contractData.contractSubject}`)
      lines.push(`签约开始：${this.contractData.signStartDate}`)
      lines.push(`签约结束：${this.contractData.signEndDate}`)
      lines.push(`合同期限：${this.calculateContractPeriod}`)
      lines.push('')
      lines.push('【租赁信息】')
      lines.push(`租赁楼宇：${this.contractData.leaseBuilding || '未填写'}`)
      lines.push(`租赁位置：${this.contractData.leaseLocation || '未填写'}`)
      lines.push(`租赁面积：${this.formatNumber(this.contractData.leaseArea)} ㎡`)
      lines.push(`租金单价：${this.formatNumber(this.contractData.firstYearRentUnitPrice)} 元/㎡/月`)
      lines.push(`租金政策：${this.contractData.rentPolicy || '未填写'}`)
      lines.push(`支付方式：${this.contractData.rentPaymentMethod || '未设置'}`)
      lines.push('')
      lines.push('【财务信息】')
      lines.push(`首年租金：${this.formatCurrency(this.contractData.firstYearRent)} 元`)
      lines.push(`月租金：${this.formatCurrency(this.monthlyRent)} 元`)
      lines.push(`押金金额：${this.formatCurrency(this.contractData.depositAmount)} 元`)
      lines.push(`押金比例：${this.depositRatio}`)
      lines.push('')
      lines.push('【开票信息】')
      lines.push(this.contractData.invoiceInfo || '未填写')
      lines.push('')
      lines.push('【备注】')
      lines.push(this.contractData.remark || '无备注')
      lines.push('')
      lines.push('【系统信息】')
      lines.push(`合同ID：${this.contractData.contractid}`)
      lines.push(`创建时间：${this.formatDateTime(this.contractData.createTime)}`)
      lines.push(`更新时间：${this.formatDateTime(this.contractData.updateTime)}`)
      lines.push('')
      lines.push('【合同状态】')
      if (this.contractStatus) {
        lines.push(`${this.contractStatus.title} - ${this.contractStatus.description}`)
      }
      lines.push('')
      lines.push('='.repeat(50))
      lines.push(`导出时间：${dayjs().format('YYYY-MM-DD HH:mm:ss')}`)
      lines.push('='.repeat(50))
      
      return lines.join('\n')
    },
    
    // 下载文件
    downloadFile(content, filename) {
      const blob = new Blob(['\ufeff' + content], { type: 'text/plain;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.contract-detail-dialog {
  max-height: 85vh;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.detail-value {
  font-weight: 500;
  color: #303133;
}

.date-value {
  color: #409EFF;
}

.money-value {
  color: #f56c6c;
  font-weight: 600;
}

.multi-line-text {
  white-space: pre-line;
  line-height: 1.6;
  color: #606266;
}

.status-info {
  margin: 20px 0;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 15px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}
</style>

<style>
.contract-detail-dialog .el-descriptions__body {
  background-color: #fafafa;
}

.contract-detail-dialog .el-descriptions-item__cell {
  padding: 12px 15px;
}

.contract-detail-dialog .el-descriptions-item__label {
  font-weight: 500;
  background-color: #f5f7fa;
  width: 120px;
}
</style>